import { useEffect, useState, useContext } from "react";
import { registerSlot, unregisterSlot } from "./AdsManager";
import { AdsContext } from "./AdSlotsProvider";
import { cn } from "src/utils/cn";

let adCount = 0;
const generateSlotId = () => `adSlot-${adCount++}`;

const getSizeMappings: (type: ads.SlotType) => ads.SizeMappings = (type) => {
  switch (type) {
    case "leaderboard":
      return [
        [[768, 0], [[728, 90]]],
        [[0, 0], [[320, 50]]],
      ];

    case "footerboard":
      return [
        [[768, 0], [[728, 90]]],
        [[0, 0], [[300, 250]]],
      ];
  }
};

const getSizes: (type: ads.SlotType) => googletag.SingleSizeArray[] = (
  type
) => {
  switch (type) {
    case "leaderboard":
      return [
        [728, 90],
        [320, 50],
      ];

    case "footerboard":
      return [
        [728, 90],
        [300, 250],
      ];
  }
};

type AdSlotProps = {
  /** Name of the ad unit in DFP */
  adUnit: string;

  type: ads.SlotType;

  /** Callback returns true if this ad slot should be allowed to refresh */
  shouldRefresh?: () => boolean;

  /** Skip the initial fetch of the ad slot is added */
  skipInitialRefresh?: boolean;

  /** How long to wait before attempting to register the slot (default: 1000) */
  settlingTime?: number;
};

const AdSlotStyles = {
  root: `relative`,
  outer: ``,
};

const ContainerStyles = {
  /* background corresponds to background used in WrapThemedHeaderBackground */
  leaderboard: `py-2 bg-brown-100/80 backdrop-blur-sm`,
  footerboard: ``,
};

const UnitStyles = {
  common: `relative my-0 mx-auto bg-black/5 overflow-hidden rounded-sm`,
  leaderboard: `w-80 h-[50px] md:w-[728px] md:h-[90px]`,
  footerboard: `w-[300px] h-[250px] mb-4 md:w-[728px] md:h-[90px] md:mb-0`,
};

export const AdSlot = ({
  adUnit,
  type,
  shouldRefresh = () => true,
  skipInitialRefresh = false,
  settlingTime = 100,
}: AdSlotProps) => {
  const [id, setId] = useState<string | undefined>();
  const [isSettled, setSettled] = useState(false);
  const { enabled } = useContext(AdsContext);

  useEffect(() => {
    const t = setTimeout(() => setSettled(true), settlingTime);
    return () => clearTimeout(t);
  });

  useEffect(() => {
    if (isSettled) {
      setId(generateSlotId());
    }
  }, [isSettled, adUnit]);

  useEffect(() => {
    if (id) {
      registerSlot({
        id,
        adUnit,
        sizes: getSizes(type),
        sizeMappings: getSizeMappings(type),
        shouldRefresh,
        skipInitialRefresh,
      });
    }
    return () => {
      if (id) {
        unregisterSlot(id);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, adUnit]);

  return enabled ? (
    <div
      className={cn([
        AdSlotStyles.root,
        AdSlotStyles.outer,
        ContainerStyles[type],
      ])}
    >
      <div
        key={id}
        id={id}
        aria-label="Advertisement"
        className={cn([UnitStyles.common, UnitStyles[type]])}
      ></div>
    </div>
  ) : null;
};

export default AdSlot;
